import PropTypes from 'prop-types';
import React, { useState } from 'react';
import ButtonV2 from 'components/ButtonV2';
import { useGetLearningHubTopics, useGetLearningHubVideos } from '../gql';
import {
  VideosSection,
  SectionHeader,
  SectionTitleContainer,
  SectionIconWrapper,
  SectionInfo,
  SectionTitle,
  SectionDescription,
  SeeAllButton,
  VideosGrid,
} from '../StyledComponents';
import TopicFiltersContainer from './TopicFiltersContainer';
import TopicIcons from './TopicIcons';
import VideoCard from './VideoCard';

const ChevronRightIcon = () => (
  <svg
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5l7 7-7 7"
    />
  </svg>
);

const HowToContent = ({ searchQuery, onPlayVideo }) => {
  const [activeTopic, setActiveTopic] = useState(null);

  const { topics, loading: topicsLoading } = useGetLearningHubTopics('howto');
  const { videos, loading: videosLoading } = useGetLearningHubVideos({
    mode: 'howto',
    search: searchQuery || undefined,
    topic_slug: activeTopic || undefined,
    limit: 200,
  });

  const loading = topicsLoading || videosLoading;

  if (loading && !videos.length) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
    );
  }

  return (
    <div>
      {/* Topic Filters */}
      <TopicFiltersContainer
        topics={topics}
        activeTopic={activeTopic}
        onSelectTopic={setActiveTopic}
      >
        <ButtonV2
          variant="pill"
          active={!activeTopic}
          onClick={() => setActiveTopic(null)}
        >
          All Topics
        </ButtonV2>
        {topics.map((topic) => (
          <ButtonV2
            variant="pill"
            key={topic.id}
            active={activeTopic === topic.id}
            onClick={() => setActiveTopic(topic.id)}
            icon={<TopicIcons icon={topic.icon} />}
          >
            {topic.name}
          </ButtonV2>
        ))}
      </TopicFiltersContainer>

      {/* Videos by Topic */}
      {!activeTopic ? (
        topics.map((topic) => {
          const topicVideos = videos.filter((v) => v.topic === topic.id);
          if (topicVideos.length === 0) return null;

          return (
            <VideosSection key={topic.id}>
              <SectionHeader>
                <SectionTitleContainer>
                  <SectionIconWrapper>
                    <TopicIcons icon={topic.icon} />
                  </SectionIconWrapper>
                  <SectionInfo>
                    <SectionTitle>{topic.name}</SectionTitle>
                    <SectionDescription>{topic.description}</SectionDescription>
                  </SectionInfo>
                </SectionTitleContainer>
                {topicVideos.length > 3 && (
                  <SeeAllButton onClick={() => setActiveTopic(topic.id)}>
                    See all {topicVideos.length} <ChevronRightIcon />
                  </SeeAllButton>
                )}
              </SectionHeader>
              <VideosGrid>
                {topicVideos.slice(0, 3).map((video) => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    onClick={onPlayVideo}
                    showLearnMore={true}
                  />
                ))}
              </VideosGrid>
            </VideosSection>
          );
        })
      ) : (
        <VideosGrid>
          {videos.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              onClick={onPlayVideo}
              showLearnMore={true}
            />
          ))}
        </VideosGrid>
      )}
    </div>
  );
};

HowToContent.propTypes = {
  searchQuery: PropTypes.string,
  onPlayVideo: PropTypes.func.isRequired,
};

export default HowToContent;
